<template>
    <section class="trailer-section">
        <header class="section-header">
            <h1 class="section-title">Lawety</h1>
            <button class="btn btn-submit" @click="openForm()">
                <font-awesome-icon icon="plus" /> Dodaj
            </button>
        </header>

        <EntityFilterBar v-model:search="searchQuery" v-model:sort="sortModel" v-model:filters="filters"
            :filters-schema="[
                { key: 'group', label: 'Grupa', type: 'select', options: groupOptions }
            ]" :sort-options="[
                { key: 'name', label: 'Nazwa' },
                { key: 'price', label: 'Cena' },
                { key: 'weight', label: 'Waga' }
            ]" />

        <PaginationControls :current-page="currentPage" :total-pages="totalPages" @update:page="goToPage" />

        <div class="status-wrapper">
            <p v-if="trailerStore.loading" class="status-loading">Ładowanie...</p>
        </div>

        <div v-if="!trailerStore.loading">
            <div v-if="filteredTrailers.length" class="trailer-list">
                <EntityCard v-for="trailer in paginatedTrailers" :key="trailer.id">
                    <template #content>
                        <div class="trailer-content">
                            <img v-if="trailer.image_url" :src="trailer.image_url" alt="Zdjęcie lawety"
                                class="trailer-image" />
                            <div class="trailer-details">
                                <div class="detail-item">
                                    <span class="detail-label">Nazwa:</span>
                                    <span>{{ trailer.name }}</span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">Grupa:</span>
                                    <span>{{ trailer.trailer_group?.name || 'Brak grupy' }}</span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">Wymiary:</span>
                                    <span>{{ trailer.width }} x {{ trailer.height }} cm</span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">Waga:</span>
                                    <span>{{ trailer.weight }} kg</span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">DMC:</span>
                                    <span>{{ trailer.dmc }} kg</span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">Cena:</span>
                                    <span>{{ trailerStore.formatPrice(trailer.price) }} zł</span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">Opis:</span>
                                    <span>{{ trailer.description }}</span>
                                </div>
                            </div>
                        </div>
                    </template>

                    <template #actions>
                        <button class="btn btn-edit" @click="showTechnicalDrawing(trailer)">
                            <font-awesome-icon icon="eye" />
                            Rysunek techniczny
                        </button>
                        <button class="btn btn-edit" @click="editTrailer(trailer)">
                            <font-awesome-icon icon="pen" />
                            Edytuj
                        </button>
                        <button class="btn btn-delete" @click="confirmDelete(trailer.id)">
                            <font-awesome-icon icon="trash" />
                            Usuń
                        </button>
                    </template>
                </EntityCard>
            </div>

            <div v-else class="empty-state">
                <font-awesome-icon icon="trailer" size="2x" />
                <p>Brak lawet do wyświetlenia</p>
            </div>
        </div>

        <PaginationControls :current-page="currentPage" :total-pages="totalPages" @update:page="goToPage" />

        <teleport to="body">
            <transition name="fade">
                <TrailerForm v-if="showForm" :visible="showForm" :trailer="editingTrailer" @close="showForm = false"
                    @saved="handleSave" />
            </transition>
        </teleport>

        <teleport to="body">
            <transition name="fade">
                <div v-if="showDrawing" class="drawing-overlay" @click.self="closeDrawing">
                    <img :src="drawingUrl" alt="Rysunek techniczny" class="drawing-fullscreen" />
                    <button class="close-drawing" @click="closeDrawing">
                        <font-awesome-icon icon="xmark" />
                    </button>
                </div>
            </transition>
        </teleport>

        <teleport to="body">
            <ConfirmDialog :visible="confirmDialog.state.isVisible" :title="confirmDialog.state.dialogOptions.title"
                :message="confirmDialog.state.dialogOptions.message"
                :confirmText="confirmDialog.state.dialogOptions.confirmText"
                :cancelText="confirmDialog.state.dialogOptions.cancelText"
                :danger="confirmDialog.state.dialogOptions.danger" @confirm="confirmDialog.handleConfirm"
                @cancel="confirmDialog.handleCancel" @close="confirmDialog.handleClose" />
        </teleport>
    </section>
</template>

<script>
import { useTrailerStore } from '@/stores/trailerStore'
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/errorUtils'
import { useConfirmDialog } from '@/composables/useConfirmDialog'
import EntityCard from '@/components/cms/EntityCard.vue'
import EntityFilterBar from '@/components/cms/EntityFilterBar.vue'
import PaginationControls from '@/components/cms/PaginationControls.vue'
import TrailerForm from './TrailerForm.vue'
import ConfirmDialog from '@/components/cms/ConfirmDialog.vue'

export default {
    components: {
        TrailerForm,
        EntityCard,
        EntityFilterBar,
        PaginationControls,
        ConfirmDialog
    },
    setup() {
        const confirmDialog = useConfirmDialog()
        return { confirmDialog }
    },
    data() {
        return {
            trailerStore: useTrailerStore(),
            searchQuery: '',
            sortModel: {
                by: '',
                order: 'asc'
            },
            filters: {
                group: ''
            },
            showForm: false,
            editingTrailer: null,

            currentPage: 1,
            itemsPerPage: 50,

            showDrawing: false,
            drawingUrl: null

        }
    },
    computed: {
        filteredTrailers() {
            let filtered = this.trailerStore.trailers

            if (this.searchQuery) {
                filtered = filtered.filter(trailer =>
                    trailer.name?.toLowerCase().includes(this.searchQuery.toLowerCase())
                )
            }

            if (this.filters.group) {
                filtered = filtered.filter(
                    trailer => trailer.trailer_group?.name === this.filters.group
                )
            }

            if (this.sortModel.by) {
                filtered = [...filtered].sort((a, b) => {
                    const fieldA = a[this.sortModel.by]
                    const fieldB = b[this.sortModel.by]
                    if (fieldA < fieldB) return this.sortModel.order === 'asc' ? -1 : 1
                    if (fieldA > fieldB) return this.sortModel.order === 'asc' ? 1 : -1
                    return 0
                })
            }

            return filtered
        },
        paginatedTrailers() {
            const start = (this.currentPage - 1) * this.itemsPerPage
            return this.filteredTrailers.slice(start, start + this.itemsPerPage)
        },
        totalPages() {
            return Math.ceil(this.filteredTrailers.length / this.itemsPerPage)
        },
        groupOptions() {
            const trailers = this.trailerStore.trailers
            const uniqueGroups = [...new Set(trailers.map(t => t.trailer_group?.name).filter(Boolean))]
            return uniqueGroups.map(name => ({
                id: name,
                name
            }))
        }
    },
    methods: {
        openForm(trailer = null) {
            this.editingTrailer = trailer
            this.showForm = true
        },
        editTrailer(trailer) {
            this.openForm(trailer)
        },
        async confirmDelete(id) {
            const confirmed = await this.confirmDialog.showConfirm({
                title: 'Usunąć lawetę?',
                message: 'Ta operacja jest nieodwracalna. Wszystkie dane związane z tą lawetą zostaną usunięte.',
                confirmText: 'Tak, usuń',
                cancelText: 'Anuluj',
                danger: true
            })

            if (confirmed) {
                try {
                    await this.trailerStore.deleteTrailer(id)
                    showSuccessToast('Usunięto lawetę.')
                } catch (err) {
                    showErrorToast(err, 'Błąd przy usuwaniu lawety.')
                }
            }
        },
        handleSave() {
            this.trailerStore.fetchTrailers()
            this.showForm = false
        },
        goToPage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page
            }
        },
        showTechnicalDrawing(trailer) {
            if (trailer.technical_drawing_url) {
                this.drawingUrl = trailer.technical_drawing_url
                this.showDrawing = true
            } else {
                showWarningToast('Brak rysunku technicznego dla tej lawety.')
            }
        },
        closeDrawing() {
            this.showDrawing = false
            this.drawingUrl = null
        }
    }
}
</script>

<style scoped lang="scss">
.trailer-section {
    max-width: 1200px;
    margin: 0 auto;
    cursor: default;
}

.section-header {
    margin-bottom: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    @media (max-width: 640px) {
        align-items: flex-start;
        flex-direction: column;
    }
}

.section-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: $color-primary-dark;
    margin: 0;
}

.status-wrapper {
    margin-bottom: 1.5rem;
}

.status-loading {
    font-size: 1rem;
    color: $color-text-light;
    text-align: center;
}

.trailer-list {
    display: grid;
    gap: 1.25rem;
}

.trailer-content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    @media (min-width: 768px) {
        flex-direction: row;
        align-items: flex-start;
    }
}

.trailer-image {
    width: 200px;
    height: 150px;
    object-fit: contain;
    border-radius: 6px;
    border: 1px solid $color-grey;
    flex-shrink: 0;
    background-color: $color-background;
}

.trailer-details {
    display: grid;
    gap: 0.5rem;
    flex: 1;
}

.detail-item {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.95rem;
}

.detail-label {
    font-weight: 600;
    color: $color-text;
    min-width: 100px;
}

.btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    svg {
        flex-shrink: 0;
    }

    &-edit {
        background: $color-primary;
        color: white;

        &:hover {
            background: $color-primary-dark;
        }
    }

    &-delete {
        background: $color-grey-light;
        color: $color-primary-accent;

        &:hover {
            background: $color-primary-accent;
            color: white;
        }
    }

    &-submit {
        background: $color-primary;
        color: white;

        &:hover {
            background: $color-primary-dark;
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 3rem 2rem;
    text-align: center;
    background: $color-white;
    border-radius: 8px;
    border: 1px solid $color-grey;

    svg {
        color: $color-grey-dark;
    }

    p {
        color: $color-text-light;
        font-size: 1.1rem;
        margin: 0;
    }
}

.drawing-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
}

.drawing-fullscreen {
    max-width: 95%;
    max-height: 95%;
    border-radius: 8px;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}

.close-drawing {
    position: absolute;
    top: 20px;
    right: 20px;
    background: $color-primary-accent;
    border: none;
    padding: 8px 10px;
    cursor: pointer;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
